import type React from "react";
import { useId } from "react";

/**
 * Иконка токена — ЕДИНАЯ на весь проект (ценники карточек, кнопки, шапка,
 * сайдбар, /account). Доработана 2026-07-24.
 *
 * ФОРМА — ИСХОДНАЯ, дизайнерская (решение владельца: «сделаем фигуру ближе к
 * той, которая была ранее — там хорошо различимые края»). Мои попытки
 * перерисовать силуэт формулой отвергнуты: тонкая талия превращала иконку в
 * «ромб» на мелких размерах, толстая читалась жирной. Оригинальный контур
 * держит края на любом кегле — трогать его нельзя.
 *
 * ОБЪЁМ добавлен поверх той же формы: базовая заливка тоном + световая грань
 * (второй слой той же фигуры со светлым градиентом, гаснущим к правому-нижнему
 * краю). Свет падает сверху-слева, как у флагманской иконки трендов. Никаких
 * «фигур внутри фигуры» — их владелец забраковал отдельно.
 *
 * Режимы (старый API сохранён — все ~30 вызовов работают без правок):
 *   • по умолчанию      — объём в брендовой гамме;
 *   • color="#fff"      — плоская заливка одним цветом (иконка на цветной
 *                         плашке: там объём только грязнит);
 *   • gradientStops=[…] — плоский двухцветный градиент (легаси-вызовы);
 *   • tone              — состояние баланса (utils/creditsState): warn — пора
 *                         пополнить, danger — на генерацию не хватает.
 */
export type CoinTone = "brand" | "warn" | "danger";

/** Тона: [светлая вершина, средний, тёмная база] + сила блика. */
const TONES: Record<CoinTone, { body: [string, string, string]; gloss: string }> = {
	brand: { body: ["#C9CFF3", "#9691D7", "#6F63BE"], gloss: "#FFFFFF" },
	warn: { body: ["#FDE68A", "#F59E0B", "#B45309"], gloss: "#FFFBEB" },
	danger: { body: ["#FECACA", "#EF4444", "#991B1B"], gloss: "#FEF2F2" },
};

/** Оригинальный контур искры (viewBox 12×11). Не менять — см. шапку. */
const SPARK =
	"M5.97215 11.2263L4.9137 8.10779C4.78284 7.72291 4.64345 7.39293 4.45903 7.01175C3.97636 6.81622 3.51378 6.66511 3.00921 6.51584L0 5.62642L2.07126 5.02813C2.87652 4.7956 3.63247 4.56306 4.42799 4.27872C4.71588 3.66069 4.91309 3.07967 5.12673 2.44868L5.95511 0C6.2704 0.679093 6.42073 1.34338 6.64776 2.03235L7.19678 3.69338C7.26373 3.89692 7.39215 4.12575 7.51267 4.30771C8.95214 4.81595 10.3423 5.17061 11.8579 5.61285C10.2833 6.17229 9.11709 6.37768 7.56258 6.95747C7.30755 7.05246 7.22721 7.44659 7.11582 7.66864L5.97155 11.2269L5.97215 11.2263Z";

type IconProps = React.SVGProps<SVGSVGElement> & {
	size?: number;
	color?: string; // однотонная заливка → плоский режим
	gradientStops?: [string, string]; // двухцветный градиент → плоский режим
	tone?: CoinTone; // состояние баланса
};

export const Coin: React.FC<IconProps> = ({
	size = 13,   // шкала проекта: ценники 13, кнопки/подписи 15, шапка 22
	width,
	height,
	color,
	gradientStops,
	tone = "brand",
	style,
	...props
}) => {
	const rawId = useId();
	const id = rawId.replace(/:/g, "");
	const flatGradient = !color && !!gradientStops;
	const volumetric = !color && !gradientStops;
	const t = TONES[tone];

	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={width ?? size}
			height={height ?? size}
			viewBox="0 0 12 11"
			fill="none"
			// Тень-подложка: на брендовых ГРАДИЕНТНЫХ кнопках сиреневая искра
			// сливалась с фоном (скрин владельца «Сгенерировать 15»). Тень
			// отделяет её от любой подложки — и от градиента, и от светлой
			// карточки. CSS-фильтр, а не feDropShadow: SVG-фильтр обрезался бы
			// границами viewBox. Вызов может перебить своим style.
			style={{ filter: "drop-shadow(0 1px 1.5px rgba(20, 16, 60, 0.35))", ...style }}
			{...props}
		>
			<defs>
				{volumetric && (
					<>
						{/* Тело: диагональ сверху-слева вниз-вправо — источник света
						    один и тот же во всём наборе иконок. */}
						<linearGradient id={`cb${id}`} x1="2.4" y1="0.6" x2="9.6" y2="10.4" gradientUnits="userSpaceOnUse">
							<stop stopColor={t.body[0]} />
							<stop offset="0.52" stopColor={t.body[1]} />
							<stop offset="1" stopColor={t.body[2]} />
						</linearGradient>
						{/* Световая грань: та же фигура, светлое пятно у верхне-левого
						    луча, полностью прозрачное к центру — читается как блик на
						    скошенной кромке, а не как вторая фигура. */}
						<linearGradient id={`cg${id}`} x1="3" y1="1" x2="7.2" y2="7.4" gradientUnits="userSpaceOnUse">
							<stop stopColor={t.gloss} stopOpacity="0.55" />
							<stop offset="0.6" stopColor={t.gloss} stopOpacity="0.08" />
							<stop offset="1" stopColor={t.gloss} stopOpacity="0" />
						</linearGradient>
					</>
				)}
				{flatGradient && (
					<linearGradient id={`cf${id}`} x1="5.92894" y1="0" x2="5.92894" y2="11.2269" gradientUnits="userSpaceOnUse">
						<stop stopColor={gradientStops![0]} />
						<stop offset="1" stopColor={gradientStops![1]} />
					</linearGradient>
				)}
			</defs>

			<path d={SPARK} fill={volumetric ? `url(#cb${id})` : flatGradient ? `url(#cf${id})` : color} />
			{volumetric && <path d={SPARK} fill={`url(#cg${id})`} />}
		</svg>
	);
};
